import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { teamApi } from "./teamApi";

const pause = (duration) => {
  return new Promise((resolve) => setTimeout(resolve, duration));
};

const userApi = createApi({
  reducerPath: "user",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BASE_ENDPOINT}api`,
    prepareHeaders: (headers, { getState }) => {
      const token = localStorage.getItem("access-token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    },
    fetchFn: async (...args) => {
      await pause(1000);
      return fetch(...args);
    },
  }),
  endpoints(builder) {
    return {
      fetchMainInformation: builder.query({
        providesTags: (result, error, arg) => ["main-informations"],
        query: () => {
          return {
            url: "/user/main-information",
            method: "GET",
          };
        },
      }),
      fetchPersonels: builder.query({
        providesTags: (result, error, arg) => ["personels"],
        query: ({ page, limit, searchText, date }) => {
          return {
            url: `user/personel-list?page=${page}&limit=${limit}&searchText=${searchText}&date=${date}`,
            method: "GET",
          };
        },
      }),
      fetchUserForUpdate: builder.query({
        providesTags: () => ["user-for-update"],
        query: (id) => {
          return {
            url: `user/get-personel-for-update/${id}`,
            method: "GET",
          };
        },
      }),
      postUser: builder.mutation({
        invalidatesTags: () => {
          return [
            "main-informations",
            "personels",
            "user-equipments-edit",
            "user-equipments",
            "user-dayoffs",
            "user-for-update",
          ];
        },
        query: (user) => {
          return {
            url: user.id === 0 ? "auth/register" : "user/personel-update",
            method: "POST",
            body: user,
          };
        },
        onCacheEntryAdded: async (args, { dispatch, queryFulfilled }) => {
          await queryFulfilled;
          dispatch(teamApi.util.invalidateTags(["user-teams"]));
        },
        transformResponse: (response, meta, arg) => response,
      }),
      updatePassword: builder.mutation({
        invalidatesTags: () => ["me"],
        query: (updatePassword) => {
          return {
            url: "user/password-update",
            method: "POST",
            body: updatePassword,
          };
        },
      }),
      enterDate: builder.mutation({
        invalidatesTags: () => ["personels", "main-informations"],
        query: (model) => {
          return {
            url: "user/enter-date",
            method: "POST",
            body: model,
          };
        },
      }),
      deleteUser: builder.mutation({
        invalidatesTags: () => {
          return ["main-informations", "personels"];
        },
        query: (id) => {
          return {
            url: `user/personel-delete?userId=${id}`,
            method: "DELETE",
          };
        },
        transformResponse: (response, meta, arg) => response,
      }),
      fetchGetUsers: builder.query({
        providesTags: (result) => ["users"],
        query: () => {
          return {
            url: "user/",
            method: "GET",
          };
        },
      }),
      fetchGetUserDetail: builder.query({
        providesTags: () => ["user"],
        query: (id) => {
          return {
            url: `user/${id}`,
            method: "GET",
          };
        },
      }),
      fetchGetUserEquipmentsForEdit: builder.query({
        providesTags: () => ["user-equipments-edit"],
        query: (userId) => {
          return {
            url: `user/equipments-edit?userId=${userId}`,
            method: "GET",
          };
        },
      }),
      fetchUserEquipments: builder.query({
        providesTags: () => ["user-equipments"],
        query: (userId) => {
          return {
            url:
              userId !== undefined
                ? `user/equipments?userId=${userId}`
                : "user/equipments",
            method: "GET",
          };
        },
      }),
      fetchUserDayoffs: builder.query({
        providesTags: () => ["user-dayoffs"],
        query: (userId) => {
          return {
            url: `userdayoffright/dayoff-rights?userId=${userId}`,
            method: "GET",
          };
        },
      }),
      fetchUsedEquipments: builder.query({
        providesTags: () => ["used-equipments"],
        query: ({ page, limit, searchText }) => {
          return {
            url: `user/used-equipments?page=${page}&limit=${limit}&searchText=${searchText}`,
            method: "GET",
          };
        },
      }),
      deleteUsedEquipment: builder.mutation({
        invalidatesTags: () => ["used-equipments"],
        query: (id) => {
          return {
            url: `user/delete-used-equipment?userEquipmentId=${id}`,
            method: "DELETE",
          };
        },
      }),
      giveBackEquipment: builder.mutation({
        invalidatesTags: () => ["used-equipments"],
        query: (model) => {
          return {
            url: `user/give-back-equipment`,
            method: "POST",
            body: model,
          };
        },
      }),
      userRightBulkUpdate: builder.mutation({
        invalidatesTags: () => ["user-dayoffs"],
        query: (model) => {
          return {
            url: "userdayoffright/bulk-update",
            method: "POST",
            body: model,
          };
        },
        onCacheEntryAdded: async (args, { dispatch, queryFulfilled }) => {
          await queryFulfilled;
          dispatch(userApi.util.invalidateTags(["personel-rights"]));
        },
      }),
    };
  },
});

export const {
  useFetchMainInformationQuery,
  useFetchPersonelsQuery,
  usePostUserMutation,
  useDeleteUserMutation,
  useUpdatePasswordMutation,
  useFetchGetUsersQuery,
  useFetchGetUserDetailQuery,
  useFetchGetUserEquipmentsForEditQuery,
  useFetchUserForUpdateQuery,
  useFetchUserEquipmentsQuery,
  useEnterDateMutation,
  useFetchUserDayoffsQuery,
  useUserRightBulkUpdateMutation,
  useFetchUsedEquipmentsQuery,
  useDeleteUsedEquipmentMutation,
  useGiveBackEquipmentMutation
} = userApi;

export { userApi };
